// @ts-nocheck
/**
 * Customer Portal Dashboard Sagas
 * Async side effects for dashboard data fetching
 */

import { all, call, put, select, takeLatest, debounce } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { customerPortalDashboardApi } from '../infrastructure/api/dashboard-api'
import { balanceApi } from '@spcc/domains/finance'
import { customerPortalDashboardActions } from './dashboard.slice'
import { selectDateRange } from './dashboard.selectors'
import type {
  DashboardData,
  RankingsData,
  LiveFeedData,
  FactoryDetailData,
  EntityDetailData,
  EntityType,
  FetchDashboardRequest,
  DateRange,
  OrdersValueKpi,
  SapBalances,
} from '../domain/types'
import { resolveDateRange } from '../domain/utils'
import { RANKINGS_TOP_N } from '../infrastructure/api/dashboard-api'

/**
 * Resolve factory IDs from location-derived factory context.
 * If a location is selected but does not map to a factory, return a non-existent
 * factory id to force empty results on factory-scoped dashboard widgets.
 */
function* resolveFactoryIds(): Generator<any, string[] | undefined, any> {
  try {
    const locationsState = yield select((state: any) => state.locations)
    const locationDerivedFactoryId = locationsState?.derivedFactoryId
    const selectedLocationId = locationsState?.currentLocationId

    if (locationDerivedFactoryId) {
      return [locationDerivedFactoryId]
    }

    if (selectedLocationId) {
      return ['__no_matching_factory__']
    }

    const factoriesState = yield select((state: any) => state.factories)
    // currentFactory is set by the global FactorySelector (selectFactory action)
    // selectedFactory is for detail views — NOT the global filter
    const factory = factoriesState?.currentFactory ?? factoriesState?.selectedFactory
    if (factory?.documentId) {
      return [factory.documentId]
    }
  } catch {
    // factories state may not exist
  }
  return undefined
}

/**
 * Build a complete request with date range + factory filter
 */
function* buildDashboardRequest(overrideRequest?: FetchDashboardRequest): Generator<any, FetchDashboardRequest, any> {
  let request: FetchDashboardRequest = overrideRequest || {}

  if (!request.from || !request.to) {
    const dateRange: DateRange = yield select(selectDateRange)
    const resolved = resolveDateRange(dateRange)
    request = { ...request, from: resolved.from, to: resolved.to }
  }

  const factoryIds: string[] | undefined = yield* resolveFactoryIds()
  if (factoryIds) {
    request = { ...request, factoryIds }
  }

  return request
}

/**
 * Fetch dashboard + rankings in parallel (2 simultaneous requests).
 * Chart data is embedded in the dashboard response to keep it lean.
 */
function* fetchDashboardSaga(action: PayloadAction<FetchDashboardRequest | undefined>) {
  try {
    const request: FetchDashboardRequest = yield* buildDashboardRequest(action.payload)

    // Fire dashboard + rankings in parallel
    const [data, rankingsData]: [DashboardData, RankingsData] = yield all([
      call(customerPortalDashboardApi.getDashboard, request),
      call(customerPortalDashboardApi.getRankings, request, RANKINGS_TOP_N),
    ])

    yield put(customerPortalDashboardActions.fetchDashboardSuccess(data))
    yield put(customerPortalDashboardActions.fetchRankingsSuccess(rankingsData))

    // Also fetch orders value + SAP balance in background (separate endpoints)
    yield put(customerPortalDashboardActions.fetchOrdersValue())
    yield* fetchSapBalanceSaga()

    // Chart data is embedded in the dashboard response
    if (data.chartData) {
      yield put(customerPortalDashboardActions.fetchChartDataSuccess(data.chartData))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch dashboard data'
    yield put(customerPortalDashboardActions.fetchDashboardFailure({ message }))
  }
}

/**
 * Refresh dashboard saga — same parallel strategy
 */
function* refreshDashboardSaga() {
  try {
    const request: FetchDashboardRequest = yield* buildDashboardRequest()

    const [data, rankingsData]: [DashboardData, RankingsData] = yield all([
      call(customerPortalDashboardApi.getDashboard, request),
      call(customerPortalDashboardApi.getRankings, request, RANKINGS_TOP_N),
    ])

    yield put(customerPortalDashboardActions.fetchDashboardSuccess(data))
    yield put(customerPortalDashboardActions.fetchRankingsSuccess(rankingsData))

    // Also refresh orders value + SAP balance
    yield put(customerPortalDashboardActions.fetchOrdersValue())
    yield* fetchSapBalanceSaga()

    if (data.chartData) {
      yield put(customerPortalDashboardActions.fetchChartDataSuccess(data.chartData))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to refresh dashboard data'
    yield put(customerPortalDashboardActions.fetchDashboardFailure({ message }))
  }
}

/**
 * Handle date range change — triggers a full dashboard fetch
 */
function* handleDateRangeChangeSaga() {
  try {
    const dateRange: DateRange = yield select(selectDateRange)
    const resolved = resolveDateRange(dateRange)
    yield put(customerPortalDashboardActions.fetchDashboard({ from: resolved.from, to: resolved.to }))
  } catch {
    // Silent fail - the fetch saga will handle errors
  }
}

/**
 * Handle factory selection change — re-fetches dashboard AND live feed immediately
 * so the live feed reflects the newly selected plant without waiting for the timer.
 */
function* handleFactoryChangeSaga() {
  try {
    const dateRange: DateRange = yield select(selectDateRange)
    const resolved = resolveDateRange(dateRange)
    yield put(customerPortalDashboardActions.fetchDashboard({ from: resolved.from, to: resolved.to }))
    yield put(customerPortalDashboardActions.fetchLiveFeed())
  } catch {
    // Silent fail
  }
}

/**
 * Fetch live feed saga — passes the current factory filter so the backend
 * returns only shipments for the selected plant.
 */
function* fetchLiveFeedSaga() {
  try {
    const factoryIds: string[] | undefined = yield* resolveFactoryIds()
    const data: LiveFeedData = yield call(customerPortalDashboardApi.getLiveFeed, factoryIds ? { factoryIds } : undefined)
    yield put(customerPortalDashboardActions.fetchLiveFeedSuccess(data))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch live feed'
    yield put(customerPortalDashboardActions.fetchLiveFeedFailure({ message }))
  }
}

/**
 * Fetch factory detail data for the drill-down drawer
 */
function* fetchFactoryDetailSaga(action: PayloadAction<{ factoryId: string; factoryName: string }>) {
  try {
    const { factoryId } = action.payload
    const request: FetchDashboardRequest = yield* buildDashboardRequest()

    const data: FactoryDetailData = yield call(
      customerPortalDashboardApi.getFactoryDetail,
      factoryId,
      request,
      RANKINGS_TOP_N
    )

    yield put(customerPortalDashboardActions.fetchFactoryDetailSuccess(data))
  } catch (error) {
    yield put(customerPortalDashboardActions.fetchFactoryDetailFailure())
  }
}

/**
 * Fetch entity detail data (customer / product / city) for the drill-down drawer
 */
function* fetchEntityDetailSaga(
  action: PayloadAction<{ entityType: EntityType; entityId: string; entityName: string }>
) {
  try {
    const { entityType, entityId } = action.payload
    const request: FetchDashboardRequest = yield* buildDashboardRequest()

    const data: EntityDetailData = yield call(
      customerPortalDashboardApi.getEntityDetail,
      entityType,
      entityId,
      request,
      RANKINGS_TOP_N
    )

    yield put(customerPortalDashboardActions.fetchEntityDetailSuccess(data))
  } catch (error) {
    yield put(customerPortalDashboardActions.fetchEntityDetailFailure())
  }
}

/**
 * Fetch orders value KPI (non-finished orders total incl. 15% VAT)
 */
function* fetchOrdersValueSaga() {
  try {
    const data: OrdersValueKpi = yield call(customerPortalDashboardApi.getOrdersValue)
    yield put(customerPortalDashboardActions.fetchOrdersValueSuccess(data))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch orders value'
    yield put(customerPortalDashboardActions.fetchOrdersValueFailure({ message }))
  }
}

/**
 * Fetch SAP contract balance (cached — does not trigger a live SAP sync).
 * Uses contractBalances[0].totalNetAmount as the customer's available balance.
 * Called directly as a sub-saga — do NOT dispatch fetchSapBalance() from here
 * or it will re-trigger the watcher and cause an infinite loop.
 */
function* fetchSapBalanceSaga() {
  try {
    const data: SapBalances = yield call(balanceApi.getSapBalancesCached)
    yield put(customerPortalDashboardActions.fetchSapBalanceSuccess(data))
  } catch {
    yield put(customerPortalDashboardActions.fetchSapBalanceFailure())
  }
}

/**
 * Root customer portal dashboard saga
 */
export function* customerPortalDashboardSaga() {
  yield takeLatest(customerPortalDashboardActions.fetchDashboard.type, fetchDashboardSaga)
  yield takeLatest(customerPortalDashboardActions.refreshDashboard.type, refreshDashboardSaga)
  yield takeLatest(customerPortalDashboardActions.setDateRange.type, handleDateRangeChangeSaga)
  yield takeLatest(customerPortalDashboardActions.setDatePreset.type, handleDateRangeChangeSaga)
  yield takeLatest(customerPortalDashboardActions.setCustomDateRange.type, handleDateRangeChangeSaga)
  yield takeLatest(customerPortalDashboardActions.fetchLiveFeed.type, fetchLiveFeedSaga)
  yield takeLatest(customerPortalDashboardActions.fetchFactoryDetail.type, fetchFactoryDetailSaga)
  yield takeLatest(customerPortalDashboardActions.fetchEntityDetail.type, fetchEntityDetailSaga)
  yield takeLatest(customerPortalDashboardActions.fetchOrdersValue.type, fetchOrdersValueSaga)
  // fetchSapBalance is called as a sub-saga by fetchDashboardSaga/refreshDashboardSaga — no watcher needed
  // Re-fetch dashboard + live feed when global factory/location selection changes (debounced)
  yield debounce(300, 'factories/selectFactory', handleFactoryChangeSaga)
  yield debounce(300, 'factories/setSelectedFactory', handleFactoryChangeSaga)
  yield debounce(300, 'factories/clearFactorySelection', handleFactoryChangeSaga)
  yield debounce(300, 'locations/selectLocation', handleFactoryChangeSaga)
  yield debounce(300, 'locations/clearLocationSelection', handleFactoryChangeSaga)
}
